import React, { useEffect, useState } from 'react';
import {
    Grid,
    TextField,
    FormControlLabel,
    Switch,
    InputAdornment,
    Typography,
    Divider,
    Box,
    IconButton,
    Button,
} from '@mui/material';
import { Title as TitleIcon, Description as DescriptionIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSurveyStore } from '../../surveyutilities/surveyStore';

const DropdownBlockProperties = ({ questionId }) => {
    const { getQuestionById, updateQuestion, validateName } = useSurveyStore();
    const question = getQuestionById(questionId);
    const [nameError, setNameError] = useState('');
    const [localName, setLocalName] = useState(question?.name || '');


    useEffect(() => {
        setLocalName(question?.name || '');
    }, [question?.name]);

    if (!question) return <Typography color="error">Question not found</Typography>;

    const handleUpdate = (property, value) => {
        updateQuestion(questionId, { [property]: value });
    };


    const handleNameChange = (value) => {
        setLocalName(value);
        const validation = validateName(value, questionId);
        setNameError(validation.error);

        if (validation.isValid) {
            updateQuestion(questionId, { name: value });
        }
    };

    const handleOptionChange = (index, field, value) => {
        const updatedOptions = [...question.options];
        updatedOptions[index] = { ...updatedOptions[index], [field]: value };
        handleUpdate('options', updatedOptions);
    };

    const addOption = () => {
        const newOption = {
            id: `opt_${Date.now()}`,
            text: `Option ${question.options.length + 1}`,
            value: `option_${question.options.length + 1}`
        };
        handleUpdate('options', [...question.options, newOption]);
    };

    const removeOption = (index) => {
        if (question.options.length > 1) {
            const updatedOptions = question.options.filter((_, i) => i !== index);
            handleUpdate('options', updatedOptions);
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Name */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Name (not visible to public, unique)"
                    value={localName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    error={!!nameError}
                    helperText={nameError}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <VisibilityOffIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>

            {/* Title */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Question Title"
                    value={question.title || ''}
                    onChange={(e) => handleUpdate('title', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Description */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={2}
                    value={question.description || ''}
                    onChange={(e) => handleUpdate('description', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon /></InputAdornment> }}
                />
            </Grid>

            {/* Placeholder */}
           <Grid size={{xs:12}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Dropdown Placeholder"
                    value={question.placeholder || ''}
                    onChange={(e) => handleUpdate('placeholder', e.target.value)}
                    helperText="Text shown when no option is selected"
                />
            </Grid>

            {/* Options */}
           <Grid size={{xs:12}}>
                <Typography variant="subtitle2" gutterBottom>
                    Options
                </Typography>
                {question.options.map((option, index) => (
                    <Box key={option.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField
                            size="small"
                            label={`Option ${index + 1}`}
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Value"
                            value={option.value}
                            onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                            sx={{ width: 120 }}
                        />
                        {question.options.length > 1 && (
                            <IconButton
                                size="small"
                                onClick={() => removeOption(index)}
                                color="error"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                ))}
                <Button
                    startIcon={<AddIcon />}
                    onClick={addOption}
                    size="small"
                    variant="outlined"
                >
                    Add Option
                </Button>
            </Grid>

            {/* Required Switch */}
           <Grid size={{xs:12}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={(e) => handleUpdate('required', e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Required Question"
                />
            </Grid>

            {/* Question ID */}
           <Grid size={{xs:12}}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" gutterBottom>
                    ID: {questionId}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default DropdownBlockProperties;